import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRollbar } from '@rollbar/react';
import isEqual from 'lodash/isEqual';

import { setProject } from '../obj/pscState';
import { getItem, setItem, removeItem } from '../utils/indexedDB';
import useAuthentication from '../hooks/useAuthentication';

const ProjectContentsContext = createContext();

export const ProjectContentsProvider = ({ children }) => {
    const rollbar = useRollbar();
    const { currentUser } = useAuthentication();
    
    const dispatch = useDispatch();
    const pscState = useSelector(state => state.pscState.value);
    const project = pscState.project;
    const config = pscState.config || {};
    
    const cacheSeconds = 30;
    const [projectID, setProjectID] = useState(0);
    const [lastUpdated, setLastUpdated] = useState(0);
    // DISABLE INDEXED DB FOR NOW
    const [isInitialized, setIsInitialized] = useState(true);
    
    const [customerDocuments, _setCustomerDocuments] = useState([]);
    const [includedDocuments, _setIncludedDocuments] = useState([]);
    const [documents, _setDocuments] = useState([]);
    const [records, _setRecords] = useState([]);
    const [deliveryLists, _setDeliveryLists] = useState([]);
    const [reports, _setReports] = useState([]);
    const [actionItemSources, _setActionItemSources] = useState([]);
    const [actionItems, _setActionItems] = useState([]);

    // Individual loaded states for each content type (start false, become true once loaded)
    const [isCustomerDocumentsLoaded, setIsCustomerDocumentsLoaded] = useState(false);
    const [isIncludedDocumentsLoaded, setIsIncludedDocumentsLoaded] = useState(false);
    const [isDocumentsLoaded, setIsDocumentsLoaded] = useState(false);
    const [isRecordsLoaded, setIsRecordsLoaded] = useState(false);
    const [isDeliveryListsLoaded, setIsDeliveryListsLoaded] = useState(false);
    const [isReportsLoaded, setIsReportsLoaded] = useState(false);
    const [isActionItemSourcesLoaded, setIsActionItemSourcesLoaded] = useState(false);
    const [isActionItemsLoaded, setIsActionItemsLoaded] = useState(false);
    
    // Track ongoing reload to prevent redundant calls
    const reloadInProgressRef = useRef(false);
    const currentReloadKeyRef = useRef(null);
    const lastReloadCompletedRef = useRef(0);

    // Helper to create a simple hash of an object
    const hashObject = useCallback((obj) => {
        if (!obj) return '0';
        const str = JSON.stringify(obj);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash.toString();
    }, []);

    // Helper to persist individual arrays to IndexedDB (separate keys for better performance)
    const persistArrayToIndexedDB = useCallback(async (key, value) => {
        // DISABLE INDEXED DB FOR NOW
        return
        
        try {
            await setItem(`project_contents_${key}`, value);
        } catch (error) {
            console.error(`Failed to persist ${key} to IndexedDB:`, error);
        }
    }, []);

    // Wrapper setters that also persist to IndexedDB (each array gets its own key)
    const setCustomerDocuments = useCallback((value) => {
        _setCustomerDocuments(value);
        persistArrayToIndexedDB('customer_documents', value);
    }, [persistArrayToIndexedDB]);

    const setIncludedDocuments = useCallback((value) => {
        _setIncludedDocuments(value);
        persistArrayToIndexedDB('included_documents', value);
    }, [persistArrayToIndexedDB]);

    const setDocuments = useCallback((value) => {
        _setDocuments(value);
        persistArrayToIndexedDB('documents', value);
    }, [persistArrayToIndexedDB]);

    const setRecords = useCallback((value) => {
        _setRecords(value);
        persistArrayToIndexedDB('records', value);
    }, [persistArrayToIndexedDB]);

    const setDeliveryLists = useCallback((value) => {
        _setDeliveryLists(value);
        persistArrayToIndexedDB('delivery_lists', value);
    }, [persistArrayToIndexedDB]);

    const setReports = useCallback((value) => {
        _setReports(value);
        persistArrayToIndexedDB('reports', value);
    }, [persistArrayToIndexedDB]);

    const setActionItemSources = useCallback((value) => {
        _setActionItemSources(value);
        persistArrayToIndexedDB('action_item_sources', value);
    }, [persistArrayToIndexedDB]);

    const setActionItems = useCallback((value) => {
        _setActionItems(value);
        persistArrayToIndexedDB('action_items', value);
    }, [persistArrayToIndexedDB]);

    // Clean up old localStorage keys (migration from old implementation)
    useEffect(() => {
        localStorage.removeItem('project_contents_project_id');
        localStorage.removeItem('project_contents_last_updated');
        localStorage.removeItem('project_contents_customer_documents');
        localStorage.removeItem('project_contents_included_documents');
        localStorage.removeItem('project_contents_documents');
        localStorage.removeItem('project_contents_records');
        localStorage.removeItem('project_contents_delivery_lists');
        localStorage.removeItem('project_contents_reports');
        localStorage.removeItem('project_action_item_sources');
        localStorage.removeItem('project_action_items');
    }, []);

    // Clean up old IndexedDB key (migration from single-key to multi-key approach)
    useEffect(() => {
        const cleanupOldIndexedDB = async () => {
            try {
                const oldData = await getItem('project_contents');
                if (oldData) {
                    console.log('ProjectContents: Removing old single-key IndexedDB entry');
                    await removeItem('project_contents');
                }
            } catch (error) {
                console.error('Failed to cleanup old IndexedDB key:', error);
            }
        };
        
        cleanupOldIndexedDB();
    }, []);

    // Load initial data from IndexedDB (separate keys)
    useEffect(() => {
        console.log("ProjectContents: Initializing from IndexedDB");
        const loadFromIndexedDB = async () => {
            try {
                // Load metadata first
                const meta = await getItem('project_contents_meta');
                if (meta) {
                    const loadedProjectID = meta.projectID || 0;
                    setProjectID(loadedProjectID);
                    setLastUpdated(meta.lastUpdated || 0);
                    
                    // Load each array separately in parallel
                    const [customerDocs, includedDocs, docs, recs, delivLists, reps, aiSources, aiItems] = await Promise.all([
                        getItem('project_contents_customer_documents'),
                        getItem('project_contents_included_documents'),
                        getItem('project_contents_documents'),
                        getItem('project_contents_records'),
                        getItem('project_contents_delivery_lists'),
                        getItem('project_contents_reports'),
                        getItem('project_contents_action_item_sources'),
                        getItem('project_contents_action_items')
                    ]);
                    
                    _setCustomerDocuments(customerDocs || []);
                    _setIncludedDocuments(includedDocs || []);
                    _setDocuments(docs || []);
                    _setRecords(recs || []);
                    _setDeliveryLists(delivLists || []);
                    _setReports(reps || []);
                    _setActionItemSources(aiSources || []);
                    _setActionItems(aiItems || []);
                    
                    console.log("ProjectContents: Loaded data for project", loadedProjectID, "from IndexedDB");
                } else {
                    console.log("ProjectContents: No cached data in IndexedDB");
                }
            } catch (error) {
                console.error('Failed to load from IndexedDB:', error);
            } finally {
                setIsInitialized(true);
            }
        };
        
        // DISABLE INDEXED DB FOR NOW
        //loadFromIndexedDB();
    }, []);

    // The _process methods are just to slim down the data we save in IndexedDB
    const __process = useCallback((row) => {
        const item = {};
        item.id = row.id;
        item.reference = row.reference;
        item.title = row.title;
        item.owner_user_id = row.owner_user_id;
        item.created_at = row.created_at;
        item.final_version_file_path = row.final_version_file_path;
        item.project_id = row.project_id;
        item.std_document = { name: row.std_document?.name, id: row.std_document?.id };
        return item;
    }, []);
    
    const _processCustomerDocument = useCallback((row) => {
        const item = __process(row);
        item.customer_document_status = { name: row.customer_document_status.name, id: row.customer_document_status.id };
        item.organisation_code = row.organisation_code;
        return item;
    }, [__process]);

    const _processIncludedDocument = useCallback((row) => {
        const item = __process(row);
        item.original = row.original;
        return item;
    }, [__process]);
    
    const _processDocument = useCallback((row) => {
        const item = __process(row);
        item.document_status = { name: row.document_status.name, id: row.document_status.id };
        return item;
    }, [__process]);

    const _processRecord = useCallback((row) => {
        const item = __process(row);
        item.record_status = { name: row.record_status.name, id: row.record_status.id };
        return item;
    }, [__process]);

    const _processDeliveryList = useCallback((row) => {
        const item = __process(row);
        item.sent_date = row.sent_date;
        item.delivered_by_user_id = row.delivered_by_user_id;
        item.delivered_to = row.delivered_to;
        item.delivery_list_status = row.delivery_list_status;
        return item;
    }, [__process]);
    
    const _processReport = useCallback((row) => {
        const item = __process(row);
        item.create_date = row.create_date;
        item.report_scheme = row.report_scheme;
        item.report_status = row.report_status;
        return item;
    }, [__process]);
    
    const _processActionItemSource = useCallback((row) => {
        const item = __process(row);
        item.name = row.name;
        item.source_date = row.source_date;
        item.default_item_type = row.default_item_type;
        item.location = row.location;
        item.action_count_total = row.action_count_total;
        item.action_count_open = row.action_count_open;
        item.action_count_closed = row.action_count_closed;
        return item;
    }, [__process]);
    
    const _processActionItem = useCallback((row) => {
        const item = __process(row);
        item.action_item_source = row.action_item_source;
        item.action_item_source_id = row.action_item_source_id;
        item.type = row.type;
        item.raised_date = row.raised_date;
        item.action_item_number = row.action_item_number;
        item.origin = row.origin;
        item.description = row.description;
        item.owner_freetext = row.owner_freetext;
        item.due_date = row.due_date;
        item.due_date_freetext = row.due_date_freetext;
        item.status = row.status;
        item.close_reference = row.close_reference;
        item.comments = row.comments;
        return item;
    }, [__process]);
    
    const _reload = useCallback((objType, list, setCb, processCb, setLoadedCb) => {
        if (!project || !project.id) {
            return Promise.resolve();
        }
        return fetch('/v0/' + objType + '?project_id=' + project.id, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) throw response;
                return response.json();
            })
            .then(function(json) {
                if (json.error && json.error === 'ENOACCESS') {
                    setCb([]);
                    if (setLoadedCb) setLoadedCb(true);
                    return;
                }
                const data = json.results.map((row) => {
                    return processCb(row);
                });
                if (!isEqual(data, list)) {
                    setCb(data);
                }
                if (setLoadedCb) setLoadedCb(true);
            })
            .catch(error => {
                console.log("Reload error", objType, error);
                if (setLoadedCb) setLoadedCb(true); // Mark as loaded even on error to avoid infinite loading
            });
    }, [project]);
    
    const _reloadCustomerDocuments = useCallback(() => _reload('customer_documents', customerDocuments, setCustomerDocuments, _processCustomerDocument, setIsCustomerDocumentsLoaded), [_reload, customerDocuments, setCustomerDocuments, _processCustomerDocument]);
    const _reloadIncludedDocuments = useCallback(() => _reload('included_documents', includedDocuments, setIncludedDocuments, _processIncludedDocument, setIsIncludedDocumentsLoaded), [_reload, includedDocuments, setIncludedDocuments, _processIncludedDocument]);
    const _reloadDocuments = useCallback(() => _reload('documents', documents, setDocuments, _processDocument, setIsDocumentsLoaded), [_reload, documents, setDocuments, _processDocument]);
    const _reloadRecords = useCallback(() => _reload('records', records, setRecords, _processRecord, setIsRecordsLoaded), [_reload, records, setRecords, _processRecord]);
    const _reloadDeliveryLists = useCallback(() => _reload('delivery_lists', deliveryLists, setDeliveryLists, _processDeliveryList, setIsDeliveryListsLoaded), [_reload, deliveryLists, setDeliveryLists, _processDeliveryList]);
    const _reloadReports = useCallback(() => _reload('reports', reports, setReports, _processReport, setIsReportsLoaded), [_reload, reports, setReports, _processReport]);
    const _reloadActionItemSources = useCallback(() => _reload('action_item_sources', actionItemSources, setActionItemSources, _processActionItemSource, setIsActionItemSourcesLoaded), [_reload, actionItemSources, setActionItemSources, _processActionItemSource]);
    const _reloadActionItems = useCallback(() => _reload('action_items', actionItems, setActionItems, _processActionItem, setIsActionItemsLoaded), [_reload, actionItems, setActionItems, _processActionItem]);

    const reloadProjectContents = useCallback(() => {
        if (typeof(project) === 'undefined' || !project.id) {
            console.log("ProjectContents: trying to reload all, but project is not initialized yet");
            return;
        }
        
        // Create a unique key for this reload based on project ID and hash of currentUser object
        // This ensures any change to user (roles, permissions, etc.) triggers a reload
        const userHash = hashObject(currentUser);
        const reloadKey = `${project.id}_${userHash}`;
        
        // If already reloading with the same key, skip this call
        if (reloadInProgressRef.current && currentReloadKeyRef.current === reloadKey) {
            console.log("ProjectContents: Reload already in progress for project", project.id, "- skipping");
            return;
        }
        
        // If the last reload completed less than 1 second ago, skip this call
        const timeSinceLastReload = Date.now() - lastReloadCompletedRef.current;
        if (timeSinceLastReload < 1000) {
            console.log("ProjectContents: Last reload completed", timeSinceLastReload, "ms ago - skipping (too soon)");
            return;
        }
        
        // If reloading with a different key, the new reload will supersede the old one
        if (reloadInProgressRef.current && currentReloadKeyRef.current !== reloadKey) {
            console.log("ProjectContents: Project or user changed - superseding previous reload");
        }
        
        // Mark reload as in progress with this key
        reloadInProgressRef.current = true;
        currentReloadKeyRef.current = reloadKey;
        
        // Reset all loaded states
        setIsCustomerDocumentsLoaded(false);
        setIsIncludedDocumentsLoaded(false);
        setIsDocumentsLoaded(false);
        setIsRecordsLoaded(false);
        setIsDeliveryListsLoaded(false);
        setIsReportsLoaded(false);
        setIsActionItemSourcesLoaded(false);
        setIsActionItemsLoaded(false);
        
        const timestamp = Date.now();
        
        // Update state
        setLastUpdated(timestamp);
        setProjectID(project.id);
        
        // Immediately write the new project ID to IndexedDB metadata
        // This ensures that if the page reloads, it has the correct project ID
        
        // DISABLE INDEXED DB FOR NOW
        //        setItem('project_contents_meta', {
        //            projectID: project.id,
        //            lastUpdated: timestamp
        //        }).catch(err => console.error('Failed to persist project ID to IndexedDB:', err));
        
        // Clear all array keys (they'll be repopulated as data arrives)
        // DISABLE INDEXED DB FOR NOW
//        Promise.all([
//            setItem('project_contents_customer_documents', []),
//            setItem('project_contents_included_documents', []),
//            setItem('project_contents_documents', []),
//            setItem('project_contents_records', []),
//            setItem('project_contents_delivery_lists', []),
//            setItem('project_contents_reports', []),
//            setItem('project_contents_action_item_sources', []),
//            setItem('project_contents_action_items', [])
//        ]).catch(err => console.error('Failed to clear arrays in IndexedDB:', err));
        
        // Now fetch the data (as it arrives, the wrapper setters will update IndexedDB with the data)
        // Wait for all fetches to complete before marking as done
        Promise.all([
            _reloadCustomerDocuments(),
            _reloadIncludedDocuments(),
            _reloadDocuments(),
            _reloadRecords(),
            _reloadDeliveryLists(),
            _reloadReports(),
            _reloadActionItemSources(),
            _reloadActionItems()
        ]).finally(() => {
            reloadInProgressRef.current = false;
            lastReloadCompletedRef.current = Date.now();
        });
    }, [project, currentUser, hashObject, _reloadCustomerDocuments, _reloadIncludedDocuments, _reloadDocuments, _reloadRecords, _reloadDeliveryLists, _reloadReports, _reloadActionItemSources, _reloadActionItems]);
    
    const clearProjectContents = useCallback(async () => {
        setProjectID(0);
        _setCustomerDocuments([]);
        _setIncludedDocuments([]);
        _setDocuments([]);
        _setRecords([]);
        _setDeliveryLists([]);
        _setReports([]);
        _setActionItemSources([]);
        _setActionItems([]);
        setLastUpdated(0);
        
        // Clear all IndexedDB keys
        // DISABLE INDEXED DB FOR NOW
//        await Promise.all([
//            setItem('project_contents_meta', { projectID: 0, lastUpdated: 0 }),
//            setItem('project_contents_customer_documents', []),
//            setItem('project_contents_included_documents', []),
//            setItem('project_contents_documents', []),
//            setItem('project_contents_records', []),
//            setItem('project_contents_delivery_lists', []),
//            setItem('project_contents_reports', []),
//            setItem('project_contents_action_item_sources', []),
//            setItem('project_contents_action_items', [])
//        ]);
    }, []);

    // Auto-reload when project changes
    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (project === null || typeof(project) === 'undefined' || typeof(project.id) === 'undefined' || typeof(projectID) === 'undefined') {
            clearProjectContents();
            return;
        }
        
        // Always reload if the project has changed
        if (project.id !== projectID) {
            // Only clear and reload if we actually had a different project loaded
            // If projectID is 0, we're just initializing, so don't clear
            if (projectID !== 0 && projectID !== project.id) {
                console.log("ProjectContents: Project ID changed from", projectID, "to", project.id, "- clearing old data and reloading");
                // Clear all data immediately (use direct setters to avoid multiple IndexedDB writes)
                // reloadProjectContents will handle the persistence with the new project ID
                _setCustomerDocuments([]);
                _setIncludedDocuments([]);
                _setDocuments([]);
                _setRecords([]);
                _setDeliveryLists([]);
                _setReports([]);
                _setActionItemSources([]);
                _setActionItems([]);
            } else if (projectID === 0) {
                console.log("ProjectContents: Initial project load for project", project.id);
            }
            // Now reload with new project data (this will update projectID and persist to IndexedDB)
            reloadProjectContents();
            return;
        }
        
        // Check if we need to refresh based on cache timeout
        if (lastUpdated + (1000 * cacheSeconds) > Date.now()) {
            return;
        }
        reloadProjectContents();
    }, [project, isInitialized, projectID, lastUpdated, cacheSeconds, clearProjectContents, reloadProjectContents]);

    // Also auto-reload when current user changes
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        reloadProjectContents();
    }, [currentUser, isInitialized, reloadProjectContents]);
    
    const _updateOrInsert = useCallback((list, data) => {
        const index = list.findIndex(item => item.id === data.id);
        if (index >= 0) {
            list[index] = data;
        } else {
            list.push(data);
        }
        return list;
    }, []);

    const _update = useCallback((input, process, list, setCb) => {
        const data = process(input);
        const updated = _updateOrInsert(list, data);
        setCb(updated);
    }, [_updateOrInsert]);

    const _delete = useCallback((id, list, setCb) => {
        const updated = list.filter(item => item.id !== id);
        setCb(updated);
    }, []);
    
    const updateCustomerDocument = useCallback((item) => _update(item, _processCustomerDocument, customerDocuments, setCustomerDocuments), [_update, _processCustomerDocument, customerDocuments, setCustomerDocuments]);
    const deleteCustomerDocument = useCallback((id) => _delete(id, customerDocuments, setCustomerDocuments), [_delete, customerDocuments, setCustomerDocuments]);
    
    const updateIncludedDocument = useCallback((item) => _update(item, _processIncludedDocument, includedDocuments, setIncludedDocuments), [_update, _processIncludedDocument, includedDocuments, setIncludedDocuments]);
    const deleteIncludedDocument = useCallback((id) => _delete(id, includedDocuments, setIncludedDocuments), [_delete, includedDocuments, setIncludedDocuments]);

    const updateDocument = useCallback((item) => _update(item, _processDocument, documents, setDocuments), [_update, _processDocument, documents, setDocuments]);
    const deleteDocument = useCallback((id) => _delete(id, documents, setDocuments), [_delete, documents, setDocuments]);
    
    const updateRecord = useCallback((item) => _update(item, _processRecord, records, setRecords), [_update, _processRecord, records, setRecords]);
    const deleteRecord = useCallback((id) => _delete(id, records, setRecords), [_delete, records, setRecords]);
        
    const updateDeliveryList = useCallback((item) => _update(item, _processDeliveryList, deliveryLists, setDeliveryLists), [_update, _processDeliveryList, deliveryLists, setDeliveryLists]);
    const deleteDeliveryList = useCallback((id) => _delete(id, deliveryLists, setDeliveryLists), [_delete, deliveryLists, setDeliveryLists]);
    
    const updateReport = useCallback((item) => _update(item, _processReport, reports, setReports), [_update, _processReport, reports, setReports]);
    const deleteReport = useCallback((id) => _delete(id, reports, setReports), [_delete, reports, setReports]);
    
    const updateActionItemSource = useCallback((item) => _update(item, _processActionItemSource, actionItemSources, setActionItemSources), [_update, _processActionItemSource, actionItemSources, setActionItemSources]);
    const deleteActionItemSource = useCallback((id) => _delete(id, actionItemSources, setActionItemSources), [_delete, actionItemSources, setActionItemSources]);

    // Sources have stats on action items, which is a PITA to sort out client-side.. so just go reload the sources #lazy #ftw
    const updateActionItem = useCallback((item) => { 
        _update(item, _processActionItem, actionItems, setActionItems); 
        _reloadActionItemSources();
    }, [_update, _processActionItem, actionItems, setActionItems, _reloadActionItemSources]);
    
    const deleteActionItem = useCallback((id) => { 
        _delete(id, actionItems, setActionItems); 
        _reloadActionItemSources();
    }, [_delete, actionItems, setActionItems, _reloadActionItemSources]);

    // When doing actions that might affect other items (for example changing Status on a Doc can change the Statuses of several other docs)
    // we do need a full reload of the contents
    const forceReloadDocuments = useCallback(async () => {
        await _reloadDocuments();
    }, [_reloadDocuments]);
    
    const forceReloadCustomerDocuments = useCallback(async () => {
        await _reloadCustomerDocuments();
    }, [_reloadCustomerDocuments]);
    
    const value = {
        // Individual loaded states for each content type
        isCustomerDocumentsLoaded,
        isIncludedDocumentsLoaded,
        isDocumentsLoaded,
        isRecordsLoaded,
        isDeliveryListsLoaded,
        isReportsLoaded,
        isActionItemSourcesLoaded,
        isActionItemsLoaded,
        
        reloadProjectContents,
        clearProjectContents,

        // Access project contents
        customerDocuments,
        includedDocuments,
        documents,
        records,
        deliveryLists,
        reports,
        actionItemSources,
        actionItems,

        //
        forceReloadDocuments,
        forceReloadCustomerDocuments,
        
        // These are all for updating IndexedDB
        updateCustomerDocument,
        deleteCustomerDocument,
        updateIncludedDocument,
        deleteIncludedDocument,
        updateDocument,
        deleteDocument,
        updateRecord,
        deleteRecord,
        updateDeliveryList,
        deleteDeliveryList,
        updateReport,
        deleteReport,
        updateActionItemSource,
        deleteActionItemSource,
        updateActionItem,
        deleteActionItem,
    };

    return (
        <ProjectContentsContext.Provider value={value}>
            {children}
        </ProjectContentsContext.Provider>
    );
};

export const useProjectContentsContext = () => {
    const context = useContext(ProjectContentsContext);
    if (!context) {
        throw new Error('useProjectContentsContext must be used within a ProjectContentsProvider');
    }
    return context;
};
